import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { type Editor } from "@tiptap/core";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { BTAvatar, btAvatarColorize, BTText } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsIconUsers } from "@buildertrend/components";

import { type DiscussionsLinkedTypes } from "types/enum";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { getDOMPositionOfSelection } from "commonComponents/btWrappers/editor/core/editor.utils";
import {
    type IMentionUserStorage,
    MentionUserTypes,
    type UserMentionSuggestion,
} from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";
import { getDiscussionTypeName } from "commonComponents/entity/discussion/Discussion.types";

import "./MentionUserSelectOverlay.less";

// Narrow to the DOM Element form so we can attach listeners directly.
const hasMount = (value: unknown): value is { mount: HTMLElement } =>
    typeof value === "object" && value !== null && "mount" in value;

const getEditorDomElement = (editor: Editor): Element | null => {
    const element = editor.options.element;
    if (element instanceof Element) return element;
    if (hasMount(element)) return element.mount;
    return null;
};

const getSuggestionDiscriminator = (user: UserMentionSuggestion) => {
    return user.mentionUserType === MentionUserTypes.Everyone ? "everyone" : user.globalUserId;
};

export interface IMentionUserSelectOverlayProps {
    editor: Editor;
    users: UserMentionSuggestion[];
    onSelectionFinalized: (user: UserMentionSuggestion) => void;
    onClose?: () => void;
    query?: string;
    // It set, disables selecting users and shows a message that mentions are not supported for the given entity type
    unsupportedEntityType?: DiscussionsLinkedTypes;
}

export function MentionUserSelectOverlay({
    editor,
    users,
    onSelectionFinalized,
    onClose,
    query = "",
    unsupportedEntityType = undefined,
}: IMentionUserSelectOverlayProps) {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const handleUserSelectionFinalized = useCallback(
        (user: UserMentionSuggestion) => {
            onSelectionFinalized(user);
        },
        [onSelectionFinalized],
    );

    useEffect(() => {
        // TipTap storage is intentionally mutable shared state —
        // `MentionUserExtension.userSuggestion.allow` reads `suggestedUsers` back to gate the popover.
        const storage = editor.storage.mentionUser as IMentionUserStorage;
        setSelectedIndex(0);
        if (storage.suggestedUsers !== users) {
            // eslint-disable-next-line react-hooks/immutability -- TipTap storage is intentionally mutable; see comment above.
            storage.suggestedUsers = users;
        }

        return () => {
            const closeStorage = editor.storage.mentionUser as IMentionUserStorage;
            closeStorage.suggestedUsers = [];
        };
    }, [editor, users]);

    useEffect(() => {
        const handleKeyDown = (event: Event) => {
            if (!users.length || unsupportedEntityType) {
                return;
            }

            const keyboardEvent = event as KeyboardEvent;
            if (keyboardEvent.key === "ArrowUp") {
                event.preventDefault();
                setSelectedIndex((selectedIndex + users.length - 1) % users.length);
                return;
            }

            if (keyboardEvent.key === "ArrowDown") {
                event.preventDefault();
                setSelectedIndex((selectedIndex + 1) % users.length);
                return;
            }

            if (keyboardEvent.key === "Enter" || keyboardEvent.key === "Tab") {
                event.preventDefault();
                if (!users[selectedIndex]) {
                    return;
                }
                handleUserSelectionFinalized(users[selectedIndex]);
            }
        };

        const editorElement = getEditorDomElement(editor);
        if (editorElement) {
            editorElement.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            if (editorElement) {
                editorElement.removeEventListener("keydown", handleKeyDown);
            }
        };
    }, [editor, users, handleUserSelectionFinalized, selectedIndex, unsupportedEntityType]);

    const { x, y, strategy, refs, update } = useFloating({
        placement: "bottom-start",
        middleware: [flip(), shift({ padding: 5 }), offset(5)],
    });

    const handleCalculatePosition = useCallback(() => {
        return getDOMPositionOfSelection(editor);
    }, [editor]);

    const onUpdate = useCallback(() => {
        // The height of the overlay can change, so we need to update the position AFTER the next frame so
        // the height is calculated once the new list of users is .
        requestAnimationFrame(() => {
            void update();
        });
    }, [update]);

    useLayoutEffect(() => {
        refs.setReference({ getBoundingClientRect: handleCalculatePosition });
    }, [handleCalculatePosition, refs]);

    useEffect(() => {
        editor.on("update", onUpdate);
        return () => {
            editor.off("update", onUpdate);
        };
    }, [editor, users, update, onUpdate]);

    const handleContentResize = (entry: ResizeObserverEntry | undefined) => {
        if (entry?.target) {
            void update();
        }
    };

    const [, onDivRefChange] = useResizeObserver<HTMLDivElement>(handleContentResize);

    useEffect(() => {
        if (!onClose) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (!refs.floating.current?.contains(event.target as Node)) {
                onClose();
            }
        };
        const handleScroll = (event: Event) => {
            const scrollElement = event.target as Element;

            // Don't close the overlay on any scroll event.
            // We ran into a Chrome issue where it would fire a scroll event for no reason, which would prematurely close the overlay.
            // Instead, we can keep it from closing unless the overlay is scrolled out of view of the editor.
            // Check if overlay is still visible within the scrolled container.
            if (refs.floating.current && scrollElement) {
                const overlayRect = refs.floating.current.getBoundingClientRect();
                const containerRect = scrollElement.getBoundingClientRect();

                // Close overlay if the overlay's bottom is above the container's top
                // OR if the overlay's top is below the container's bottom
                if (
                    overlayRect.top > containerRect.bottom ||
                    overlayRect.bottom < containerRect.top
                ) {
                    onClose();
                    return;
                }
            }

            // If the position is still visible within the editor, update the position
            onUpdate();
        };

        const scrollableEditorElement = getEditorDomElement(editor);
        window.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        scrollableEditorElement?.addEventListener("scroll", handleScroll, true);

        return () => {
            window.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
            scrollableEditorElement?.removeEventListener("scroll", handleScroll, true);
        };
    }, [editor, onClose, refs.floating, update, handleCalculatePosition, onUpdate]);

    // Render a name with the matching query part bolded
    const getHighlightedName = useCallback(
        (user: UserMentionSuggestion) => {
            const testId = `mentionUserSelectOverlayName-${getSuggestionDiscriminator(user)}`;
            const name =
                user.mentionUserType === MentionUserTypes.Everyone ? "Everyone" : user.name;
            // If no query or empty query, just return the name
            if (!query?.trim()) {
                return <BTText size="normal-sm" text={name} testid={testId} />;
            }

            const normalizedQuery = query.toLowerCase().trim();
            const normalizedName = name.toLowerCase();
            const matchIndex = normalizedName.indexOf(normalizedQuery);
            // If no match found, return the name without highlighting
            if (matchIndex === -1) {
                return <BTText size="normal-sm" text={name} testid={testId} />;
            }

            // Split into three parts: before match, match, and after match
            const beforeMatch = name.substring(0, matchIndex);
            const matchedPart = name.substring(matchIndex, matchIndex + normalizedQuery.length);
            const afterMatch = name.substring(matchIndex + normalizedQuery.length);

            return (
                <span data-testid={testId}>
                    {beforeMatch && <BTText size="normal-sm" text={beforeMatch} />}
                    <BTText className="MatchedPart" size="normal-sm" text={matchedPart} />
                    {afterMatch && <BTText size="normal-sm" text={afterMatch} />}
                </span>
            );
        },
        [query],
    );

    function getListItemDescriptor(user: UserMentionSuggestion) {
        return `mentionUserSelectOverlayItem-${getSuggestionDiscriminator(user)}`;
    }

    return (
        <div
            ref={(ref) => {
                refs.setFloating(ref);
                onDivRefChange(ref);
            }}
            className="MentionUserSelectOverlay"
            style={{
                position: strategy,
                top: y ?? undefined,
                left: x ?? undefined,
            }}
            role="listbox"
            aria-label="User mention suggestions"
            aria-activedescendant={
                users.length > 0 ? getListItemDescriptor(users[selectedIndex]) : undefined
            }
            aria-keyshortcuts="Up Down Enter Tab"
            tabIndex={0}
        >
            <ul className="MentionUserSelectOverlayList" aria-label={`${users.length} users found`}>
                {unsupportedEntityType && (
                    <li className="MentionUserSelectOverlayItem">
                        <BTText
                            size="normal-sm"
                            text={`@Mentions aren't supported on ${getDiscussionTypeName(
                                unsupportedEntityType,
                                true,
                            )} right now`}
                        />
                    </li>
                )}
                {!unsupportedEntityType &&
                    users.map((user) => {
                        return (
                            <li
                                key={getSuggestionDiscriminator(user)}
                                data-testid={getListItemDescriptor(user)}
                                id={getListItemDescriptor(user)}
                                className={cn("MentionUserSelectOverlayItem", {
                                    Selected: user === users[selectedIndex],
                                })}
                                role="option"
                                aria-selected={user === users[selectedIndex]}
                                onClick={() => {
                                    handleUserSelectionFinalized(user);
                                }}
                            >
                                {user.mentionUserType === MentionUserTypes.Everyone ? (
                                    <div className="MentionUserEveryoneItem">
                                        <BdsIconUsers
                                            testid="mentionUserSelectOverlayIcon-everyone"
                                            size="sm"
                                            className="MentionUserIcon"
                                        />
                                        <div className="MentionUserEveryoneText">
                                            {getHighlightedName(user)}
                                            <BTText
                                                size="normal-sm"
                                                text={`Mention ${user.count ?? users.length} users`}
                                                className="MentionUserEveryoneCount"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <BTRow align="middle" wrap={false} gutter={8}>
                                        <BTCol>
                                            <BTAvatar
                                                testid={`mentionUserSelectOverlayAvatar-${user.globalUserId}`}
                                                className="MentionUserAvatar"
                                                text={user.name}
                                                size="xs"
                                                colorize={
                                                    btAvatarColorize[
                                                        user.globalUserId % btAvatarColorize.length
                                                    ]
                                                }
                                            />
                                        </BTCol>
                                        <BTCol className="MentionUserText">
                                            {getHighlightedName(user)}
                                        </BTCol>
                                    </BTRow>
                                )}
                            </li>
                        );
                    })}
                {users.length === 0 && !unsupportedEntityType && (
                    <li className="MentionUserSelectOverlayItem">
                        <BTText size="normal-sm" text="No users found" />
                    </li>
                )}
            </ul>
        </div>
    );
}
